import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

interface ElementSize {
  height: number;
  width: number;
}

/**
 * Tracks an element's size with a `ResizeObserver`. Returns a callback ref and
 * the latest size, which is `undefined` until the element is measured. `width`
 * is the content width; `height` is the border-box height.
 */
export function useElementSize() {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [size, setSize] = useState<ElementSize>();

  useEffect(() => {
    if (!element) {
      setSize(undefined);
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      const next = {
        height: entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height,
        width: entry.contentRect.width,
      };
      setSize((current) =>
        current &&
        current.height === next.height &&
        current.width === next.width
          ? current
          : next,
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return [setElement, size] as const;
}

/**
 * Tracks whether a CSS media query currently matches.
 */
export function useMediaQuery(query: string) {
  const subscribe = useCallback(
    (notify: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', notify);
      return () => list.removeEventListener('change', notify);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}
